"use client";

import { useMemo } from "react";
import { useTable } from "spacetimedb/react";
import { tables } from "@/module_bindings";
import { mapTaxExemption } from "@/lib/stdb-map";
import { useResolvedCurrentOrganization } from "./use-current-organization";
import type { TaxExemption } from "@/types/tax-exemption";

export function useTaxExemption(orgId?: string): {
  taxExemption: TaxExemption | null;
  isReady: boolean;
} {
  const current = useResolvedCurrentOrganization();
  const effectiveOrgId = orgId ?? current.organization?.id;

  const [rows, exemptionReady] = useTable(tables.org_tax_exemptions);

  const taxExemption = useMemo(() => {
    if (!effectiveOrgId) return null;
    const row = rows
      .filter((r) => r.organizationId === effectiveOrgId)
      .sort((a, b) => {
        // New submissions supersede rejected applications. Stable id tie-break
        // keeps imported duplicates deterministic across live subscription order.
        const left = a.updatedAt?.toDate().getTime() ?? 0;
        const right = b.updatedAt?.toDate().getTime() ?? 0;
        return left === right
          ? b.id.localeCompare(a.id)
          : left > right
            ? -1
            : 1;
      })[0];
    return row ? mapTaxExemption(row) : null;
  }, [rows, effectiveOrgId]);

  const isReady = orgId ? exemptionReady : exemptionReady && current.isReady;

  return { taxExemption, isReady };
}
